import {ApolloClient, InMemoryCache} from '@apollo/client'
import {setContext} from '@apollo/client/link/context'
import {onError} from '@apollo/client/link/error'
import {createUploadLink} from 'apollo-upload-client'
import type {FixType} from '@ambler/shared'
import {CONNECT_AS_HEADER, readConnectAsSessionId} from '@ambler/shared-front'
import {ErrorCodeType} from '../../../constants/enum'
import Sentry, {generateTraceId} from '../../../lib/sentry'
import {API_VERSION_HEADERS} from '../../../lib/api'
import {notifySessionRefused, refusesSession} from '../../../lib/auth/session'

const ssrMode = !process.browser

export function createApolloClient(initialState: FixType): ApolloClient<FixType> {
  const traceId = generateTraceId()

  // Read per request: a tab enters and leaves impersonation without reloading.
  const connectAsLink = setContext((_, {headers}) => {
    const connectAsSessionId = readConnectAsSessionId()

    return connectAsSessionId ? {headers: {...headers, [CONNECT_AS_HEADER]: connectAsSessionId}} : {headers}
  })

  const uploadLink = createUploadLink({
    uri: process.env.GRAPHQL_ENDPOINT, // Server URL (must be absolute)
    // The API is another origin of the same site: without this the browser
    // keeps the session cookie to itself.
    credentials: 'include',
    headers: {
      'x-ambler-client-trace-id': traceId,
      'x-ambler-client-location': !ssrMode ? window.location.href : 'ssr', // TODO: remove from CORS as well
      ...API_VERSION_HEADERS,
    },
  })

  // ? ssr is not for authenticated calls at the moment
  const link = ssrMode ? uploadLink : connectAsLink.concat(uploadLink)
  const errorLink = onError(({operation, graphQLErrors, networkError}) => {
    if (networkError) {
      console.error('networkError', networkError)
      return
    }

    // ? Find any unknown error.
    if (graphQLErrors) {
      graphQLErrors.forEach(err => {
        const isKnownError =
          (err.extensions?.code && Object.keys(ErrorCodeType).includes((err.extensions as any).code)) || false

        if (!isKnownError) {
          Sentry.withScope(scope => {
            scope.setTag('operation', operation.operationName)
            scope.setTag('client_trace_id', traceId)
            Sentry.captureException(new Error(`${operation.operationName}: ${err.message}`))
          })
        }
      })
    }
    if (refusesSession(graphQLErrors)) {
      notifySessionRefused()
    }
  })

  return new ApolloClient({
    connectToDevTools: process.browser,
    ssrMode, // Disables forceFetch on the server (so queries are only run once)
    link: !ssrMode ? errorLink.concat(link) : link,
    name: `${process.env.AMBLER_STAGE}_BO`,
    version: process.env.version,
    cache: new InMemoryCache().restore(initialState || {}),
  })
}
